import React, { useState, useEffect, useCallback } from "react";
import { Button, Modal, Flowbite } from "flowbite-react";
import CommonHeader from "../components/CommonHeader";
import ToastNotification, { showToast } from "../components/Toaster";
import usersApi from "../services/users/UserApi"; // Import UserApi
import PillButton from "../components/button/Pills";

const BotIdsPage: React.FC = () => {
  const [botIds, setBotIds] = useState<Bot[]>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedBot, setSelectedBot] = useState<Bot | null>(null);
  const [botForm, setBotForm] = useState({id:0, botId: "", clientSecret: "" });
  const [formErrors, setFormErrors] = useState<{ botId?: string; clientSecret?: string }>({});
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [selectedAssignBot, setSelectedAssignBot] = useState<Bot | null>(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [userList, setUserList] = useState<{ id: string; name: string }[]>([]);
  const [openUserDetailsModal, setOpenUserDetailsModal] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState<{ id: number; name: string } | null>(null);
  const [loading, setLoading] = useState(false); // Add loading state

  const fetchBots = useCallback(async () => {
    setLoading(true); // Set loading to true before API call
    try {
      const response = await usersApi.getRcsBots();
      if (response.success) {
        const bots: Bot[] = response.value.data.contents.map((bot: { id: number; botId: string; clientSecret: string; assigned: boolean; user: any }): Bot => ({
          id: bot.id,
          botId: bot.botId,
          clientSecret: bot.clientSecret,
          assigned: bot.assigned,
          user: bot.user
            ? { id: bot.user.id.toString(), username: bot.user.username } // Ensure user is an object with id and username
            : null,
        }));
        setBotIds(bots);
      } else {
        showToast("Failed to fetch bots.", "error");
      }
    } catch (error) {
      showToast("An error occurred while fetching bots.", "error");
    } finally {
      setLoading(false); // Set loading to false after API call
    }
  }, []); // Add useCallback to memoize fetchBots

  const fetchUserList = async () => {
    try {
      const response = await usersApi.getNoBotsUser(); // Fetch the user list
      if (response.success) {
        const assignedUserIds = botIds
          .filter((bot) => bot.assigned)
          .map((bot) => bot.user?.toString());
        setUserList(
          response.value.data.contents
            .filter((user: any) => !assignedUserIds.includes(user.id.toString()))
            .map((user: any) => ({
              id: user.id.toString(),
              name: user.username,
            }))
        );
      } else {
        showToast("Failed to fetch user list.", "error");
      }
    } catch (error) {
      showToast("An error occurred while fetching the user list.", "error");
    }
  };

  useEffect(() => {
    // Fetch bots on component mount
    fetchBots();
  }, [fetchBots]); // Add fetchBots as a dependency

  interface Bot {
    id: number;
    botId: string;
    clientSecret: string;
    assigned: boolean; // Added assigned property
    user: { id: string; username: string } | null; // Updated user property to an object or null
  }

  const validateForm = (): boolean => {
    const errors: { botId?: string; clientSecret?: string } = {};

    if (!botForm.botId.trim()) {
      errors.botId = "Bot ID is required.";
    }

    if (!botForm.clientSecret.trim()) {
      errors.clientSecret = "Client Secret is required.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0; // Return true if no errors
  };

  const handleDeleteClick = (bot: Bot): void => {
    setSelectedBot(bot);
    setOpenDeleteModal(true);
  };

  const handleDelete = async () => {
    if (selectedBot) {
      try {
        const response = await usersApi.deleteRcsBot(selectedBot.id);
        if (response.success) {
          setBotIds(botIds.filter((bot) => bot.id !== selectedBot.id));
          showToast("Bot deleted successfully!", "success");
        } else {
          showToast("Failed to delete bot.", "error");
        }
      } catch (error) {
        showToast("An error occurred while deleting the bot.", "error");
      }
      setOpenDeleteModal(false);
    }
  };

  const handleAddClick = () => {
    setIsEditing(false);
    setBotForm({id:0, botId: "", clientSecret: "" });
    setOpenAddEditModal(true);
  };

  const handleEditClick = (bot: { id: number, botId: string, clientSecret: string }): void => {
    setIsEditing(true);
    setBotForm(bot);
    setOpenAddEditModal(true);
  };

  const handleSaveBot = async () => {
    if (!validateForm()) {
      return;
    }
    try {
      if (isEditing) {
        const payload = {        
          id: botForm.id,
          botId: botForm.botId,
          clientSecret: atob(botForm.clientSecret),
        };
        const response = await usersApi.updateRcsBot(payload);
        if (response.success) {
          showToast("Bot updated successfully!", "success");
        } else {
          showToast("Failed to update bot.", "error");
        }
      } else {
        const payload = {
          botId: botForm.botId,
          clientSecret: atob(botForm.clientSecret),
        };
        const response = await usersApi.setRcsBot(payload);
        if (response.success) {

          showToast("Bot added successfully!", "success");
        } else {
          showToast("Failed to add bot.", "error");
        }
      }
    } catch (error) {
      showToast("An error occurred while saving the bot.", "error");
    } finally {
      fetchBots(); // Refetch bots after adding/editing
    }
    setOpenAddEditModal(false);
  };

  const handleAssignClick = (bot: Bot) => {
    setSelectedAssignBot(bot);
    setOpenAssignModal(true);
    fetchUserList(); // Fetch the user list when opening the modal
  };

  const handleAssignBot = async () => {
    if (selectedAssignBot && selectedUserId) {
      try {
        const response = await usersApi.assignBotToUser({
          botId: selectedAssignBot.id,
          userId: selectedUserId,
        });
        if (response.success) {
          showToast("Bot assigned successfully!", "success");
          fetchBots(); // Refresh the bot list
          setOpenAssignModal(false);
        } else {
          showToast("Failed to assign bot.", "error");
        }
      } catch (error) {
        showToast("An error occurred while assigning the bot.", "error");
      }
    }
  };

  const handleUnassignBot = async () => {
    if (selectedAssignBot) {
      try {      
          const response = await usersApi.unassignBotFromUser(Number(selectedUserDetails?.id));     
        if (response.success) {
          showToast("Bot unassigned successfully!", "success");         
        } else {
          showToast("Failed to unassign bot.", "error");
        }
      } catch (error) {
        showToast("An error occurred while unassigning the bot.", "error");
      } finally {
        fetchBots(); // Refresh the bot list
        setOpenUserDetailsModal(false); // Close the user details modal
      }
    }
  };

  return (
    <Flowbite>
      <div className="flex w-full h-[calc(100vh-67px)]">
        <ToastNotification />
        <div className="flex-1">
          <CommonHeader title="Bot IDs" buttonText="Add Bot Id" onButtonClick={handleAddClick} />

          {loading ? ( // Display "Loading..." when loading is true
            <p className="text-gray-500 text-center dark:text-gray-50 mt-[200px]">Loading...</p>
          ) : botIds.length === 0 ? (
            <p className="text-gray-500 text-center dark:text-gray-50 mt-[200px]">No records found.</p>
          ) : (
            <div className="w-full p-16">
              <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
                <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-900 dark:text-gray-400">
                    <tr>
                      <th scope="col" className="px-6 py-3">ID</th>
                      <th scope="col" className="px-6 py-3">Bot Id</th>
                      <th scope="col" className="px-6 py-3">Client Secret</th>
                      <th scope="col" className="px-6 py-3">Assigned</th>
                      <th scope="col" className="px-6 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {botIds.map((bot) => (
                      <tr key={bot.id} className="bg-white border-b dark:bg-gray-700 dark:border-gray-700">
                        <td className="px-6 py-4 dark:text-gray-50">{bot.id}</td>
                        <td className="px-6 py-4 dark:text-gray-50">{bot.botId}</td>
                        <td className="px-6 py-4 dark:text-gray-50">{atob(bot.clientSecret)}</td>
                        <td className="px-6 py-4 dark:text-gray-50">
                          {bot.assigned ? (
                            <button
                              type="button"
                              className="text-blue-500 underline"
                              onClick={() => {
                                setSelectedUserDetails({
                                  id: Number(bot.user?.id) || 0, // Ensure the id is a number
                                  name: bot.user?.username || "Unknown", // Extract the username
                                });
                                setSelectedAssignBot(bot);
                                setOpenUserDetailsModal(true);
                              }}
                            >
                              Assigned
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="text-red-500 underline"
                              onClick={() => handleAssignClick(bot)}
                            >
                              Unassigned
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            type="button"
                            title="Edit bot"
                            className="action-btn"
                            onClick={() => handleEditClick({ id: bot.id, botId: bot.botId, clientSecret: bot.clientSecret })}
                          >
                             <svg
                              stroke="currentColor"
                              fill="none"
                              stroke-width="2"
                              viewBox="0 0 24 24"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              height="15px"
                              width="15px"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                          <button
                            type="button"
                            title="Delete bot"
                            className="action-btn ml-2"
                            onClick={() => handleDeleteClick(bot)}
                          >
                            <svg
                              stroke="currentColor"
                              fill="currentColor"
                              strokeWidth="0"
                              viewBox="0 0 448 512"
                              height="1em"
                              width="1em"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16zM53.2 467a48 48 0 0 0 47.9 45h245.8a48 48 0 0 0 47.9-45L416 128H32z"></path>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Delete Modal */}
        <Modal show={openDeleteModal} size="md" onClose={() => setOpenDeleteModal(false)} popup>
           <Modal.Header className="dark:bg-gray-900 bg-gray-200 rounded-t-lg ">Delete Bot</Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <p className="dark:text-gray-50 text-gray-800">Are you sure you want to delete this bot?</p>
              <div className="flex justify-center gap-4 mt-4">
                <PillButton variant="danger"  onClick={handleDelete}>Yes</PillButton>
                <PillButton variant="primary" onClick={() => setOpenDeleteModal(false)}>Cancel</PillButton>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* Add/Edit Modal */}
        <Modal show={openAddEditModal} size="md" onClose={() => setOpenAddEditModal(false)} popup>
           <Modal.Header className="dark:bg-gray-900 bg-gray-200 rounded-t-lg">{isEditing ? "Edit Bot Id" : "Add Bot Id"}</Modal.Header>
          <Modal.Body>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 dark:text-gray-200 mb-1">
                  Bot ID
                </label>
                <input
                  type="text"
                  placeholder="Bot ID"
                  value={botForm.botId}
                  onChange={(e) => setBotForm({ ...botForm, botId: e.target.value })}
                  className="w-full p-2 border rounded dark:bg-gray-800 dark:text-gray-100"
                />
                {formErrors.botId && <p className="text-red-500 text-sm">{formErrors.botId}</p>}
              </div>
              <div>
                <label className="block text-gray-700 dark:text-gray-200 mb-1">
                  Client Secret
                </label>
                <input
                  type="text"
                  placeholder="Client Secret"
                  value={botForm.clientSecret ? atob(botForm.clientSecret) : ""} // Safely decode Client Secret from Base64
                  onChange={(e) =>
                    setBotForm({
                      ...botForm,
                      clientSecret: btoa(e.target.value), // Encode the Client Secret back to Base64
                    })
                  }
                  className="w-full p-2 border rounded dark:bg-gray-800 dark:text-gray-100"
                />
                {formErrors.clientSecret && <p className="text-red-500 text-sm">{formErrors.clientSecret}</p>}
              </div>
              <div className="flex justify-end gap-4">
                <PillButton variant="dark"  onClick={handleSaveBot}>
                {isEditing ? "Update": "Save"}
                </PillButton>
                <PillButton variant="primary" onClick={() => setOpenAddEditModal(false)}>
                  Cancel
                </PillButton>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* Assign Modal */}
        <Modal show={openAssignModal} size="md" onClose={() => setOpenAssignModal(false)} popup>
          <Modal.Header  className="dark:bg-gray-900 bg-gray-200 rounded-t-lg">Assign Bot</Modal.Header>
          <Modal.Body>
            <div className="space-y-4">
              <p className="dark:text-gray-50">Bot: {selectedAssignBot?.botId}</p>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full p-2 border rounded dark:bg-gray-800 dark:text-gray-100"
              >
                <option value="">Select User</option>
                {userList.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
              <div className="flex justify-end gap-4">
                <Button className="bg-blue-500 text-white" onClick={handleAssignBot}>
                  Assign
                </Button>
                <Button color="gray" onClick={() => setOpenAssignModal(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* User Details Modal */}
        <Modal
          show={openUserDetailsModal}
          size="md"
          onClose={() => setOpenUserDetailsModal(false)}
          popup
        >
          <Modal.Header  className="dark:bg-gray-900 bg-gray-200 rounded-t-lg">User Details</Modal.Header>
          <Modal.Body className="dark:bg-gray-600 bg-white rounded-b-lg">
            <div className="space-y-4">
              <p className="dark:text-gray-100">User ID: {selectedUserDetails?.id}</p>
              <p className="dark:text-gray-100">Username: {selectedUserDetails?.name}</p>
              <div className="flex justify-end gap-4">
                <PillButton
                  className="dark:bg-red-500 bg-red-500 dark:text-white text-white hover:dark:bg-red-700 hover:bg-red-700"
                  onClick={handleUnassignBot}
                >
                  Unassign
                </PillButton>
                <PillButton
                
                  onClick={() => setOpenUserDetailsModal(false)}
                >
                  Close
                </PillButton>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </Flowbite>
  );
};

export default BotIdsPage;
